export function getTimezoneAbbr(timezone: string, date?: Date): string {
  const d = date || new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(d);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    const abbr = tzPart?.value || timezone;
    if (abbr.startsWith('GMT')) {
      const city = timezone.split('/').pop()?.replace(/_/g, ' ');
      if (city) return city;
    }
    return abbr;
  } catch {
    return timezone;
  }
}

function parseTimeString(time: string): { h: number; m: number } {
  const parts = time.split(':').map(Number);
  return { h: parts[0] || 0, m: parts[1] || 0 };
}

function getTimezoneOffsetMs(timezone: string, refDate: Date): number {
  const utcStr = refDate.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = refDate.toLocaleString('en-US', { timeZone: timezone });
  return new Date(tzStr).getTime() - new Date(utcStr).getTime();
}

function wallClockToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string
): Date {
  const fakeUtc = new Date(Date.UTC(year, month, day, hour, minute, 0));
  const offsetMs = getTimezoneOffsetMs(timezone, fakeUtc);
  return new Date(fakeUtc.getTime() - offsetMs);
}

export function parseDateTimeInTimezone(dateTimeStr: string, timezone: string): Date {
  // Strip any timezone suffix — our scheduled_at stores wall-clock time
  // as pseudo-UTC in a timestamptz column, so we always interpret the
  // bare hours/minutes as being in the given timezone.
  const bare = dateTimeStr.replace(/Z$/, '').replace(/[+-]\d{2}(:\d{2})?$/, '');
  const match = bare.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return new Date(dateTimeStr);
  const [, y, mo, d, h, mi] = match;
  return wallClockToUtc(
    parseInt(y),
    parseInt(mo) - 1,
    parseInt(d),
    parseInt(h),
    parseInt(mi),
    timezone
  );
}

export function formatTimeWithTz(
  time: string,
  dateStr: string,
  timezone: string
): string {
  const { h, m } = parseTimeString(time);
  try {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(5, 7)) - 1;
    const day = parseInt(dateStr.substring(8, 10));
    const utcDate = wallClockToUtc(year, month, day, h, m, timezone);

    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(utcDate);
    const abbr = getTimezoneAbbr(timezone, utcDate);
    return `${formatted} ${abbr}`;
  } catch {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }
}

export function convertTimeToTimezone(
  time: string,
  dateStr: string,
  fromTimezone: string,
  toTimezone: string
): string {
  const { h, m } = parseTimeString(time);
  if (isNaN(h) || isNaN(m)) return time;

  try {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(5, 7)) - 1;
    const day = parseInt(dateStr.substring(8, 10));
    const utcDate = wallClockToUtc(year, month, day, h, m, fromTimezone);

    return new Intl.DateTimeFormat('en-US', {
      timeZone: toTimezone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(utcDate);
  } catch {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }
}

export function formatDateTimeForEmail(
  scheduledAt: string,
  displayTimezone: string,
  sourceTimezone?: string
): { dateStr: string; timeStr: string } {
  // sourceTimezone = the TZ the wall-clock value is stored in (guide's TZ)
  // displayTimezone = the TZ to render the output in
  // When omitted, sourceTimezone defaults to displayTimezone (same TZ for both)
  const interpretTz = sourceTimezone || displayTimezone;
  const date = parseDateTimeInTimezone(scheduledAt, interpretTz);
  const abbr = getTimezoneAbbr(displayTimezone, date);
  try {
    const dateStr = new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
    const timeStr =
      new Intl.DateTimeFormat('en-US', {
        timeZone: displayTimezone,
        hour: 'numeric',
        minute: '2-digit',
      }).format(date) +
      ' ' +
      abbr;
    return { dateStr, timeStr };
  } catch {
    return {
      dateStr: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
      timeStr: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
  }
}

export function formatCalendarTimestamp(utcDate: Date): string {
  return utcDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}
